// Ny ordre (/create-order/:customerNumber), i startsidens stil (planer/ny-kundesamhandling-plan.md).
// Nås fra «Ny ordre» på startsiden (velg kunde først) og fra kundekortet.
// Med ?type=skiplukk er det et skiplukk (kunden trenger hjelp til å finne ski), ikke en ordre:
// skipar i stedet for vare, og en oppfølger. Se src/skiplukk/felles.js.
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, knappPrimar } from './ny-kundesamhandling/Skjema';
import Ikon from './ny-kundesamhandling/Ikon';
import ParSkjema from './skiplukk/ParSkjema';
import { BRUK, tomtPar, tilLagring, manglerFelt } from './skiplukk/felles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


function CreateOrder() {
  const { customerNumber } = useParams();
  const navigate = useNavigate();
  const skiplukk = new URLSearchParams(useLocation().search).get('type') === 'skiplukk';
  const [par, setPar] = useState(() => [tomtPar()]);
  const [oppfolger, setOppfolger] = useState('');

  const [employees, setEmployees] = useState([]);
  const [kunde, setKunde] = useState(null);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  // «Send SMS med kundelenke»: kunden ser varen og status og kan sende melding (/kundeordre/…).
  // Av som standard i første omgang (vinnsel 29.10).
  const [sendLenke, setSendLenke] = useState(false);

  // State for å lagre ordredetaljer inkludert valgt ansatt og butikkid
  const [formData, setFormData] = useState({
    Varemerke: '',
    Produkt: '',
    Størrelse: '',
    Farge: '',
    Kommentar: '',
    Ansatt: Cookies.get('selectedEmployee') || '',
    butikkid: Cookies.get('butikkid') || '',
    kundeid: parseInt(customerNumber, 10),
    ordreid: '',
  });

  useEffect(() => {
    const fetchLastOrderId = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/orders/last-order-id`);
        const data = await response.json();
        const lastOrderId = parseInt(data.lastOrderId, 10);
        const nextOrderId = isNaN(lastOrderId) ? 1 : lastOrderId + 1;

        setFormData(prevData => ({
          ...prevData,
          ordreid: nextOrderId
        }));
      } catch (error) {
        console.error('Feil ved henting av siste ordre-ID:', error);
      }
    };

    const fetchEmployees = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/employees`);
        const data = await response.json();
        setEmployees(data);
      } catch (error) {
        console.error('Feil ved henting av ansatte:', error);
      }
    };

    // Kunden ordren gjelder, så den ansatte ser at det er riktig kunde (som OrderDetails).
    const fetchKunde = async () => {
      try {
        const nr = parseInt(customerNumber, 10);
        const response = await fetch(`${API_BASE_URL}/customers?customerNumber=${encodeURIComponent(nr)}`);
        if (!response.ok) return;
        const data = await response.json();
        setKunde((Array.isArray(data) ? data : []).find((c) => c.customerNumber === nr) || null);
      } catch (error) {
        console.error('Feil ved henting av kunden:', error);
      }
    };

    fetchLastOrderId();
    fetchEmployees();
    fetchKunde();
  }, [customerNumber]);

  // Skiplukk: første par er som regel til kunden selv, så navnet fylles inn (kan endres).
  useEffect(() => {
    if (!skiplukk || !kunde) return;
    const navn = [kunde.firstName, kunde.lastName].filter(Boolean).join(' ').slice(0, 60);
    if (!navn) return;
    setPar((liste) => (liste[0] && !liste[0].hvem ? [{ ...liste[0], hvem: navn }, ...liste.slice(1)] : liste));
  }, [skiplukk, kunde]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'Ansatt') {
      const selectedEmployee = employees.find(emp => emp.navn === value);

      // Oppdater valgt ansatt i state og i cookies
      setFormData({
        ...formData,
        [name]: value,
        butikkid: selectedEmployee ? selectedEmployee.butikkid : ''
      });

      // Oppdater cookies med valgt ansatt og butikkid
      Cookies.set('selectedEmployee', value);
      if (selectedEmployee && selectedEmployee.butikkid) {
        Cookies.set('butikkid', selectedEmployee.butikkid);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lagrer) return;
    if (skiplukk) {
      const mangler = manglerFelt(par) || (!String(formData.Kommentar || '').trim() ? 'Skriv en beskjed til den som følger opp.' : '');
      if (mangler) {
        setFeil(mangler);
        return;
      }
    }
    setLagrer(true);
    setFeil('');

    // Skiplukk: Varemerke/Produkt fylles, så det ser riktig ut i lister som bare viser dem.
    const skiplukkFelt = skiplukk
      ? {
          type: 'skiplukk',
          Varemerke: 'Skiplukk',
          Produkt: par.map((p) => BRUK.find((b) => b.verdi === p.bruk)?.tittel).filter(Boolean).join(' + ') || 'Ski',
          Størrelse: '',
          Farge: '',
          Oppfolger: oppfolger,
          skiplukk: tilLagring(par),
        }
      : {};

    const newOrder = {
      ...formData,
      ...skiplukkFelt,
      ordreid: formData.ordreid,
      butikkid: Cookies.get('butikkid') || formData.butikkid,
      registrertDato: new Date().toLocaleString('no-NO', { timeZone: 'Europe/Oslo' }), // Bruker lokal tid
      status: 'Aktiv',
      endretdato: '',
    };

    try {
      const response = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newOrder),
      });

      if (response.ok) {
        const addedOrder = await response.json();
        let smsLenke = null;
        if (sendLenke && kunde?.phoneNumber) {
          try {
            const r = await fetch(`${API_BASE_URL}/orders/${addedOrder._id}/send-lenke`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ansatt: formData.Ansatt }),
            });
            smsLenke = r.ok ? 'sendt' : 'feil';
          } catch {
            smsLenke = 'feil';
          }
        }
        navigate(`/order-details/${addedOrder._id}`, { state: { smsLenke } });
        return;
      }
      console.error('Feil ved registrering av ordre:', response.status);
      setFeil(skiplukk ? 'Klarte ikke å registrere skiplukket. Prøv igjen.' : 'Klarte ikke å registrere ordren. Prøv igjen.');
    } catch (error) {
      console.error('Feil ved kommunikasjon med serveren:', error);
      setFeil('Fikk ikke kontakt med serveren. Prøv igjen.');
    }
    setLagrer(false);
  };

  const kundeNavn = kunde ? [kunde.firstName, kunde.lastName].filter(Boolean).join(' ') : '';

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      {kunde ? <Tilbake til={`/customer-details/${kunde._id}`}>Tilbake til kundekortet</Tilbake> : <Tilbake />}

      {skiplukk ? (
        <Sidetopp
          ikon="snofnugg"
          farge="lager"
          tittel="Nytt skiplukk"
          undertittel={`${formData.ordreid ? `Skiplukk nr. ${formData.ordreid} · ` : ''}Kunden trenger hjelp til å finne riktige ski. Dette er ikke en vareordre.`}
        />
      ) : (
        <Sidetopp
          ikon="kvittering"
          farge="betaling"
          tittel="Registrer ordre"
          undertittel={formData.ordreid ? `Ordrenr. ${formData.ordreid}` : 'Bestille vare til kunde'}
        />
      )}

      {kunde && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 p-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-700 text-white">
            <Ikon navn="person" className="w-5 h-5" />
          </span>
          <p className="min-w-0 flex-1 basis-48 text-slate-800">
            <span className="block text-sm text-slate-600">{skiplukk ? 'Skiplukket gjelder' : 'Ordren gjelder'}</span>
            <span className="block font-semibold [overflow-wrap:anywhere]">{kundeNavn || 'Uten navn'}</span>
            <span className="block font-mono text-sm">{kunde.normalizedPhone || kunde.phoneNumber}</span>
            {skiplukk && <span className="block text-sm [overflow-wrap:anywhere]">{kunde.email || 'Ingen e-post registrert'}</span>}
          </p>
          {/* Skiplukk: den som følger opp skal kunne nå kunden, så kontaktinfo må stemme. */}
          {skiplukk && (
            <Link to={`/edit-customer/${kunde._id}`} className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900">
              Endre navn, telefon eller e-post
            </Link>
          )}
        </div>
      )}
      {skiplukk && kunde && !kunde.email && (
        <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-1.5 text-amber-900">
          Kunden har ingen e-post. Spør om den, og legg den inn på kunden hvis kunden vil ha forslag på e-post.
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {skiplukk ? (
          <>
            <p className="text-sm text-slate-700">Felt med <span className="text-red-700">*</span> må fylles ut. Velg også bruk for hvert par.</p>
            <ParSkjema idPrefiks="ny-skiplukk" par={par} onEndre={setPar} kundeNavn={kundeNavn} />
            <Tekstfelt
              id="ny-ordre-kommentar"
              etikett="Beskjed til den som følger opp"
              hjelp="Hva kunden ønsker, når kunden kan komme tilbake, om ski må bestilles osv."
              flerlinjet
              name="Kommentar"
              value={formData.Kommentar}
              onChange={handleChange}
              required
            />
          </>
        ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <Tekstfelt id="ny-ordre-merke" etikett="Varemerke" name="Varemerke" value={formData.Varemerke} onChange={handleChange} required />
          <Tekstfelt id="ny-ordre-produkt" etikett="Produkt" name="Produkt" value={formData.Produkt} onChange={handleChange} required />
          <Tekstfelt id="ny-ordre-str" etikett="Størrelse" name="Størrelse" value={formData.Størrelse} onChange={handleChange} required />
          <Tekstfelt id="ny-ordre-farge" etikett="Farge" name="Farge" value={formData.Farge} onChange={handleChange} required />
        </div>
        )}
        {!skiplukk && <Tekstfelt id="ny-ordre-kommentar" etikett="Kommentar" flerlinjet name="Kommentar" value={formData.Kommentar} onChange={handleChange} />}
        <Valgfelt id="ny-ordre-ansatt" etikett={skiplukk ? 'Ansvarlig' : 'Ansatt'} name="Ansatt" value={formData.Ansatt} onChange={handleChange} required>
          <option value="">Velg en ansatt</option>
          {employees.map(employee => (
            <option key={employee._id} value={employee.navn}>
              {employee.navn}
            </option>
          ))}
        </Valgfelt>
        {skiplukk && (
          <div>
            <Valgfelt id="ny-skiplukk-oppfolger" etikett="Følges opp av" value={oppfolger} onChange={(e) => setOppfolger(e.target.value)} aria-describedby="ny-skiplukk-oppfolger-hjelp">
              <option value="">Ingen valgt ennå</option>
              {employees.filter((e) => e.navn !== formData.Ansatt).map((employee) => (
                <option key={employee._id} value={employee.navn}>{employee.navn}</option>
              ))}
            </Valgfelt>
            <p id="ny-skiplukk-oppfolger-hjelp" className="mt-1 text-sm text-slate-600">
              Den som kan hjelpe kunden. Den ansatte får beskjed når de logger inn, og skiplukket står under
              «Dine aktive ordrer» både hos den ansvarlige og hos den som følger opp.
            </p>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white px-3 py-1.5">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5"
              checked={sendLenke && !!kunde?.phoneNumber}
              disabled={!kunde?.phoneNumber}
              onChange={(e) => setSendLenke(e.target.checked)}
              aria-describedby="ny-ordre-lenke-hjelp"
            />
            <span>
              <span className="font-medium">Send SMS med kundelenke</span>
              <span id="ny-ordre-lenke-hjelp" className="block text-sm text-slate-600">
                {kunde?.phoneNumber
                  ? `Kunden får en lenke der hen ser ${skiplukk ? 'status' : 'varen og status'} og kan sende dere melding.`
                  : 'Kunden har ikke telefonnummer.'}
              </span>
            </span>
          </label>
        </div>

        {feil && (
          <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>
        )}

        <button type="submit" disabled={lagrer} className={`${knappPrimar} w-full sm:w-auto`}>
          <Ikon navn="hake" className="w-6 h-6" />
          {lagrer ? 'Registrerer …' : skiplukk ? 'Registrer skiplukk' : 'Registrer ordre'}
        </button>
      </form>
    </div>
  );
}

export default CreateOrder;
